/**
 * @fileoverview Business Logic (BL) for FanMessages (Mensajes a la afición).
 * Encapsulates client validations, sanitization, default parameter handling,
 * and delegates calls to the Data Layer (DL).
 * 
 * @module utilities/BL/FanMessages
 */

import * as DataLayerFanMessages from './../DL/FanMessages';

/**
 * Retrieves the full list of active fan messages.
 * 
 * @async
 * @function BLGetMessages
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} API response payload.
 */
export const BLGetMessages = async () => {
    try {
        return await DataLayerFanMessages.GetMessages();
    } catch (error) {
        return { success: false, message: error?.message || 'Error al obtener los mensajes.' };
    }
};

/**
 * Retrieves a single fan message by its ID.
 * 
 * @async
 * @function BLGetMessage
 * @param {number} messageId - Unique message ID.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const BLGetMessage = async (messageId) => {
    if (!messageId || Number(messageId) <= 0) {
        return { success: false, message: 'El identificador del mensaje es inválido.' };
    }
    try {
        return await DataLayerFanMessages.GetMessage(Number(messageId));
    } catch (error) {
        return { success: false, message: error?.message || 'Error al obtener el mensaje.' };
    }
};

/**
 * Validates, sanitizes and saves a fan message.
 * 
 * @async
 * @function BLSaveMessage
 * @param {Object} messageData - Fan message attributes and configuration.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const BLSaveMessage = async (messageData) => {
    if (!messageData || typeof messageData !== 'object') {
        return { success: false, message: 'La información del mensaje es obligatoria.' };
    }

    if (!messageData.title || typeof messageData.title !== 'string' || messageData.title.trim() === '') {
        return { success: false, message: 'El título descriptivo del mensaje es obligatorio.' };
    }

    if (!messageData.mainText || typeof messageData.mainText !== 'string' || messageData.mainText.trim() === '') {
        return { success: false, message: 'El texto principal a proyectar es obligatorio.' };
    }

    const sanitizedFormatId = Number(messageData.formatId);
    const validFormatId = !isNaN(sanitizedFormatId) && sanitizedFormatId >= 1 && sanitizedFormatId <= 11
        ? sanitizedFormatId
        : 1;

    const sanitizedPayload = {
        ...messageData,
        id: messageData.id ? Number(messageData.id) : 0,
        title: messageData.title.trim(),
        formatId: validFormatId,
        mainText: messageData.mainText.trim(),
        subtitleText: messageData.subtitleText ? messageData.subtitleText.trim() : null,
        backgroundType: messageData.backgroundType || 'transparent',
        backgroundUrl: messageData.backgroundUrl || null,
        backgroundColor: messageData.backgroundColor || null,
        leftImageUrl: messageData.leftImageUrl || null,
        leftQrUrl: messageData.leftQrUrl ? messageData.leftQrUrl.trim() : null,
        rightImageUrl: messageData.rightImageUrl || null,
        rightQrUrl: messageData.rightQrUrl ? messageData.rightQrUrl.trim() : null,
        textColor: messageData.textColor || '#FFFFFF',
        subtitleColor: messageData.subtitleColor || '#FFFFFF',
        fontFamily: messageData.fontFamily || 'Montserrat',
        fontSizePx: Number(messageData.fontSizePx) > 0 ? Number(messageData.fontSizePx) : 48,
        offsetY: !isNaN(Number(messageData.offsetY)) ? Number(messageData.offsetY) : 0
    };

    try {
        return await DataLayerFanMessages.SaveMessage(sanitizedPayload);
    } catch (error) {
        return { success: false, message: error?.message || 'Error al procesar el guardado del mensaje.' };
    }
};

/**
 * Validates and deletes a fan message by its ID.
 * 
 * @async
 * @function BLDeleteMessage
 * @param {number} messageId - Unique message ID.
 * @returns {Promise<{success: boolean, message?: string}>} API response payload.
 */
export const BLDeleteMessage = async (messageId) => {
    if (!messageId || Number(messageId) <= 0) {
        return { success: false, message: 'El identificador del mensaje a eliminar es inválido.' };
    }
    try {
        return await DataLayerFanMessages.DeleteMessage(Number(messageId));
    } catch (error) {
        return { success: false, message: error?.message || 'Error al eliminar el mensaje.' };
    }
};

/**
 * Clones an existing fan message into a new record.
 * 
 * @async
 * @function BLDuplicateMessage
 * @param {number} messageId - Source message ID.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const BLDuplicateMessage = async (messageId) => {
    if (!messageId || Number(messageId) <= 0) {
        return { success: false, message: 'El identificador del mensaje a duplicar es inválido.' };
    }
    try {
        return await DataLayerFanMessages.DuplicateMessage(Number(messageId));
    } catch (error) {
        return { success: false, message: error?.message || 'Error al duplicar el mensaje.' };
    }
};

/**
 * Selects an active message for stadium projection.
 * 
 * @async
 * @function BLSelectMessage
 * @param {number} messageId - Message ID.
 * @param {string} [targetOverlay='all'] - Target overlay ('all', 'messages', 'selfiefan').
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const BLSelectMessage = async (messageId, targetOverlay = 'all') => {
    if (!messageId || Number(messageId) <= 0) {
        return { success: false, message: 'Selecciona un mensaje válido para continuar.' };
    }
    try {
        return await DataLayerFanMessages.SelectMessage(Number(messageId), targetOverlay || 'all');
    } catch (error) {
        return { success: false, message: error?.message || 'Error al seleccionar el mensaje.' };
    }
};

/**
 * Toggles projection visibility on overlays.
 * 
 * @async
 * @function BLToggleVisibility
 * @param {number|null} messageId - Message ID to project, or null to hide active.
 * @param {boolean} isVisible - Projection state.
 * @param {string} [targetOverlay='all'] - Target overlay ('all', 'messages', 'selfiefan').
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const BLToggleVisibility = async (messageId, isVisible, targetOverlay = 'all') => {
    try {
        return await DataLayerFanMessages.ToggleVisibility(
            messageId ? Number(messageId) : null,
            Boolean(isVisible),
            targetOverlay || 'all'
        );
    } catch (error) {
        return { success: false, message: error?.message || 'Error al alternar visibilidad de pantalla.' };
    }
};

/**
 * Retrieves the global projection configuration from memory.
 * 
 * @async
 * @function BLGetGlobalConfig
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const BLGetGlobalConfig = async () => {
    try {
        return await DataLayerFanMessages.GetGlobalConfig();
    } catch (error) {
        return { success: false, message: error?.message || 'Error al consultar la configuración global.' };
    }
};

/**
 * Updates the global projection configuration in memory.
 * 
 * @async
 * @function BLUpdateGlobalConfig
 * @param {Object} configData - Configuration payload.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const BLUpdateGlobalConfig = async (configData) => {
    if (!configData || typeof configData !== 'object') {
        return { success: false, message: 'Los datos de configuración son obligatorios.' };
    }
    try {
        return await DataLayerFanMessages.UpdateGlobalConfig(configData);
    } catch (error) {
        return { success: false, message: error?.message || 'Error al actualizar la configuración global.' };
    }
};

/**
 * Packs a single file into FormData and uploads it to the backend for optimization.
 * 
 * @async
 * @function BLUploadMedia
 * @param {File|Blob} fileObject - Media file to upload.
 * @param {string} [mediaCategory=''] - Media category ('background', 'logo', 'side', 'qr', 'avatar').
 * @returns {Promise<{success: boolean, url?: string, message?: string}>} API response payload.
 */
export const BLUploadMedia = async (fileObject, mediaCategory = '') => {
    if (!fileObject) {
        return { success: false, message: 'No se ha seleccionado ningún archivo.' };
    }

    const multipartFormData = new FormData();
    multipartFormData.append('file', fileObject);
    if (mediaCategory) {
        multipartFormData.append('type', mediaCategory);
    }

    try {
        return await DataLayerFanMessages.UploadMedia(multipartFormData);
    } catch (error) {
        return { success: false, message: error?.message || 'Error al subir el archivo multimedia.' };
    }
};

/**
 * Packs multiple files into FormData and uploads them in batch.
 * 
 * @async
 * @function BLUploadBatchMedia
 * @param {Array<File|Blob>|FileList} filesCollection - Array or FileList of media files.
 * @param {string} [mediaCategory=''] - Category identifier.
 * @returns {Promise<{success: boolean, urls?: Array<string>, count?: number, message?: string}>} API response payload.
 */
export const BLUploadBatchMedia = async (filesCollection, mediaCategory = '') => {
    if (!filesCollection || filesCollection.length === 0) {
        return { success: false, message: 'No se han proporcionado archivos para la carga en lote.' };
    }

    const multipartFormData = new FormData();
    for (let index = 0; index < filesCollection.length; index++) {
        multipartFormData.append('files', filesCollection[index]);
    }
    if (mediaCategory) {
        multipartFormData.append('type', mediaCategory);
    }

    try {
        return await DataLayerFanMessages.UploadBatchMedia(multipartFormData);
    } catch (error) {
        return { success: false, message: error?.message || 'Error al subir el lote de archivos.' };
    }
};

// Ergonomic aliases
export const GetMessages = BLGetMessages;
export const GetMessage = BLGetMessage;
export const SaveMessage = BLSaveMessage;
export const DeleteMessage = BLDeleteMessage;
export const DuplicateMessage = BLDuplicateMessage;
export const SelectMessage = BLSelectMessage;
export const ToggleVisibility = BLToggleVisibility;
export const GetGlobalConfig = BLGetGlobalConfig;
export const UpdateGlobalConfig = BLUpdateGlobalConfig;
export const UploadMedia = BLUploadMedia;
export const UploadBatchMedia = BLUploadBatchMedia;
